/* eslint-disable */
// Resuelve el tipo operativo de un device a uno de los slugs canónicos
// del catálogo MapV2. Cada slug tiene su SVG curado en
// `theming/generated/<tema>/device_<slug>_<status>.svg` y un color del
// container del Sistema B (azul/violeta/dorado/naranja/rosa/rojo/...).
//
// Estrategia (primera que match gana):
//   1. item.attributes.deviceType (string) si ya es uno de los slugs
//      conocidos. Permite override explícito desde el form de Devices.
//   2. item.category (string) si ya es uno de los slugs conocidos.
//   3. Inferencia heurística por keywords en categoryData.name. El
//      catálogo legacy del backend (`/v2/device-categories`) tiene
//      nombres descriptivos como "PATRULLA MUNICIPAL ZINACANTEPEC" o
//      "AMBULANCIA CRUZ ROJA", suficientes para inferir el slug.
//   4. Fallback a 'otros' (gris neutro).

var KNOWN_SLUGS = {
  policia_municipal: 1,
  policia_estatal: 1,
  policia_ministerial: 1,
  gn: 1,
  transito: 1,
  ambulancia: 1,
  bomberos: 1,
  proteccion_civil: 1,
  transporte_publico: 1,
  funcionario: 1,
  dron: 1,
  otros: 1,
}

// Etiqueta humana del slug — usada en tooltips, subtitles y headers de
// drawer para que el operador identifique el tipo de unidad sin tener
// que abrir Datos Administrativos. Mantener sincronizado con KNOWN_SLUGS.
var SLUG_LABELS = {
  policia_municipal: 'Policía Municipal',
  policia_estatal: 'Policía Estatal',
  policia_ministerial: 'Policía Ministerial',
  gn: 'Guardia Nacional',
  transito: 'Tránsito',
  ambulancia: 'Ambulancia',
  bomberos: 'Bomberos',
  proteccion_civil: 'Protección Civil',
  transporte_publico: 'Transporte Público',
  funcionario: 'Funcionario',
  dron: 'Dron',
  otros: 'Sin clasificar',
}

export function deviceTypeLabel(slug) {
  return SLUG_LABELS[slug] || 'Sin clasificar'
}

// Keywords por slug. Orden importa: el primer match gana, así que
// patrones más específicos van arriba (ej. 'GUARDIA NACIONAL' antes
// que 'POLICIA' para evitar que GN caiga en policía estatal/municipal).
//
// Las keywords se buscan en MAYÚSCULAS contra el name del
// categoryData del backend. Pueden incluir variantes con/sin acentos
// porque el match es por indexOf en string normalizado.
var KEYWORD_GROUPS = [
  {
    slug: 'gn',
    patterns: ['GUARDIA NACIONAL', 'G.N.', 'GN ', 'SEDENA', 'MILITAR', 'EJERCITO', 'EJÉRCITO', 'MARINA', 'SEMAR'],
  },
  {
    slug: 'ambulancia',
    patterns: ['AMBULANCIA', 'AMBULANCE', 'CRUZ ROJA', 'SUEM', 'PARAMEDIC', 'MÉDIC', 'MEDIC', 'SALUD'],
  },
  {
    slug: 'bomberos',
    patterns: ['BOMBERO', 'BOMBA', 'INCENDIO'],
  },
  {
    slug: 'proteccion_civil',
    patterns: ['PROTECCION CIVIL', 'PROTECCIÓN CIVIL', 'PCIVIL', 'PC ', 'CIVIL ', 'RESCATE'],
  },
  {
    slug: 'transito',
    patterns: ['TRANSITO', 'TRÁNSITO', 'VIALIDAD', 'VIAL ', 'GRUA', 'GRÚA'],
  },
  {
    slug: 'policia_ministerial',
    patterns: ['MINISTERIAL', 'FGJ', 'FISCALIA', 'FISCALÍA', 'STJ', 'PROCURADURIA', 'PROCURADURÍA', 'INVESTIG'],
  },
  {
    slug: 'policia_estatal',
    patterns: [
      'ESTATAL', 'CES ', ' CES', 'POLICIA ESTATAL', 'POLICÍA ESTATAL',
      'SSE ', 'SSC ', 'COMISION ESTATAL', 'COMISIÓN ESTATAL',
      // PEC = Policia Estatal Caminera (EdoMex). Identificadores tipo
      // "PEC-67739" se ven en operacion test; sin esto caen a 'otros'.
      'PEC-', 'PEC ', 'PE-', 'PE ',
    ],
  },
  {
    slug: 'dron',
    patterns: ['DRON', 'DRONE', 'UAV', 'AEREO', 'AÉREO'],
  },
  {
    slug: 'policia_municipal',
    // Fallback general: "POLICIA" / "POLICÍA" sin más contexto cae aquí
    // (la municipal es la corporación más numerosa típicamente).
    patterns: ['MUNICIPAL', 'POLICIA MUNICIPAL', 'POLICÍA MUNICIPAL', 'SSP MUNICIPAL', 'PATRULLA', 'POLICIA', 'POLICÍA'],
  },
  {
    slug: 'transporte_publico',
    patterns: ['TRANSPORTE', 'AUTOBUS', 'AUTOBÚS', 'BUS ', 'CAMION ', 'CAMIÓN', 'COMBI', 'TAXI', 'MOVILIDAD'],
  },
  {
    slug: 'funcionario',
    patterns: ['FUNCIONARIO', 'OFICIAL', 'ADMINISTRATIVO', 'DIRECTIVO', 'COMISARIO', 'COMANDANTE', 'SECRETARIO'],
  },
]

function _normalizeSlug(val) {
  if (!val || typeof val !== 'string') return null
  var s = val.toLowerCase().trim().replace(/\s+/g, '_').replace(/-/g, '_')
  return KNOWN_SLUGS[s] ? s : null
}

function _inferFromName(name) {
  if (!name || typeof name !== 'string') return null
  var upper = name.toUpperCase()
  for (var i = 0; i < KEYWORD_GROUPS.length; i++) {
    var group = KEYWORD_GROUPS[i]
    for (var j = 0; j < group.patterns.length; j++) {
      if (upper.indexOf(group.patterns[j]) !== -1) return group.slug
    }
  }
  return null
}

// Memo de shapes vistos (por categoryData.name) para debug. Expuesto en
// window.__klnDeviceShapes para que el usuario inspeccione desde
// consola qué shape no está matcheando bien.
var _seenShapes = Object.create(null)

/**
 * Devuelve el slug del tipo operativo del device. Nunca retorna null —
 * fallback garantizado a 'otros' para que el marker tenga icono.
 */
export function resolveDeviceType(item) {
  if (!item) return 'otros'

  // 1. Override explícito desde el form (futuro: campo opcional en
  //    devices.model.js para forzar slug cuando la heurística falla).
  var attrSlug = item.attributes && _normalizeSlug(item.attributes.deviceType)
  if (attrSlug) {
    _recordShape(item, attrSlug, 'attributes.deviceType')
    return attrSlug
  }

  // 2. Slug directo en item.category (cuando el backend lo envía
  //    canónico, no como ObjectId del catálogo legacy).
  var direct = _normalizeSlug(item.category)
  if (direct) {
    _recordShape(item, direct, 'category')
    return direct
  }

  // 3. Inferencia por nombre del catálogo enriquecido. El catálogo
  //    legacy `/v2/device-categories` trae nombres descriptivos.
  var name = (item.categoryData && item.categoryData.name) || item.categoryName
  var fromName = _inferFromName(name)
  if (fromName) {
    _recordShape(item, fromName, 'name-heuristic')
    return fromName
  }

  // 4. Inferencia por el nombre del device mismo cuando el catalogo no
  //    esta enriquecido. Caso real (EdoMex test): backend manda devices
  //    con name='CRUZ ROJA - MEX-708' pero categoryData=null y category
  //    como ObjectId crudo. Sin esto, todos los CRUZ ROJA caian a 'otros'
  //    (circulo gris). Se usa solo si los pasos 1-3 fallaron — el name
  //    del device es menos confiable que el catalogo, asi que vive abajo.
  var fromDeviceName = _inferFromName(item.name)
  if (fromDeviceName) {
    _recordShape(item, fromDeviceName, 'device-name-heuristic')
    return fromDeviceName
  }

  // 5. Fallback genérico.
  _recordShape(item, 'otros', 'fallback')
  return 'otros'
}

function _recordShape(item, resolvedSlug, via) {
  if (typeof window === 'undefined') return
  var cd = item.categoryData || {}
  var key = cd.name || item.category || '__no_name__'
  if (_seenShapes[key]) return
  _seenShapes[key] = {
    name: cd.name,
    category: item.category,
    deviceName: item.name,
    resolvedSlug: resolvedSlug,
    via: via,
  }
  window.__klnDeviceShapes = _seenShapes
}

// Lista de slugs exportada para iterar desde el generator y desde el
// toolbar (toggles por tipo). Mantener en orden lógico de presentación.
export var DEVICE_TYPE_SLUGS = [
  'policia_municipal',
  'policia_estatal',
  'policia_ministerial',
  'gn',
  'transito',
  'ambulancia',
  'bomberos',
  'proteccion_civil',
  'transporte_publico',
  'funcionario',
  'dron',
  'otros',
]

// Mapping slug → ícono interno compartido. Las 4 policías + tránsito
// comparten 'patrulla' (color del container las distingue). GN tiene
// variante 'patrulla_franja'. Vehículos especializados tienen ícono
// propio. El generator usa este mapping para combinar container + ícono.
export var DEVICE_TYPE_INNER_ICON = {
  policia_municipal: 'patrulla',
  policia_estatal: 'patrulla',
  policia_ministerial: 'patrulla',
  transito: 'patrulla',
  gn: 'patrulla_franja',
  ambulancia: 'ambulancia',
  bomberos: 'bomberos',
  proteccion_civil: 'proteccion_civil',
  transporte_publico: 'bus',
  funcionario: 'sedan',
  dron: 'dron',
  otros: 'generico',
}

// Mapping slug → ícono Material Design (v-icon). Usado donde se necesita un
// glyph de fuente (no el SVG del marker): header del drawer, tooltips, etc.
// El dron usa mdi-quadcopter; antes el drawer mostraba siempre mdi-car-emergency
// (del config genérico de device) aunque el marker del mapa sí lo distinguía.
export var DEVICE_TYPE_MDI_ICON = {
  policia_municipal: 'mdi-car-emergency',
  policia_estatal: 'mdi-car-emergency',
  policia_ministerial: 'mdi-car-emergency',
  transito: 'mdi-car-emergency',
  gn: 'mdi-shield-account',
  ambulancia: 'mdi-ambulance',
  bomberos: 'mdi-fire-truck',
  proteccion_civil: 'mdi-medical-bag',
  transporte_publico: 'mdi-bus',
  funcionario: 'mdi-car',
  dron: 'mdi-quadcopter',
  otros: 'mdi-car-emergency',
}

export function deviceTypeMdiIcon(slug) {
  return DEVICE_TYPE_MDI_ICON[slug] || 'mdi-car-emergency'
}
